import { useEffect, useState } from "react";

import type { Subscription } from "../interfaces/user-subscription";
import {
  checkRevenueCatSubscription,
  hasActiveSubscription,
  initializeRevenueCat,
  initializeRevenueCatAnonymous,
  isRevenueCatInitialized,
} from "../utils/revenuecat";
import { Sentry } from "../utils/sentry";

interface UseRevenueCatReturn {
  isInitialized: boolean;
  subscription: Subscription | null;
  hasActiveSubscription: boolean;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useRevenueCat = (userId: string | null): UseRevenueCatReturn => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [hasActive, setHasActive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscriptionData = async () => {
    if (!userId) return;

    try {
      setIsLoading(true);
      setError(null);

      // Initialize RevenueCat if not already initialized
      if (!isRevenueCatInitialized()) {
        await initializeRevenueCat(userId);
        setIsInitialized(isRevenueCatInitialized());
      }

      // Check for active subscription
      const [subscriptionData, hasActiveData] = await Promise.all([
        checkRevenueCatSubscription(),
        hasActiveSubscription(),
      ]);

      setSubscription(subscriptionData);
      setHasActive(hasActiveData);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to fetch subscription data",
      );
      console.error("Error fetching RevenueCat data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const initializeForAnonymousUser = async () => {
    try {
      setError(null);

      // Check if already initialized to avoid unnecessary re-initialization
      const alreadyInitialized = isRevenueCatInitialized();
      if (alreadyInitialized) {
        setIsInitialized(true);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      await initializeRevenueCatAnonymous();
      setIsInitialized(isRevenueCatInitialized());
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to initialize for anonymous user",
      );
      console.error("Error initializing RevenueCat for anonymous user:", err);
      Sentry.captureException(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Only run if we haven't initialized yet
    if (userId) {
      if (!isInitialized) {
        fetchSubscriptionData();
      }
    } else {
      // Handle unauthenticated users - initialize for anonymous checkout
      if (!isInitialized) {
        initializeForAnonymousUser();
      }
    }
  }, [userId, isInitialized]);

  const refetch = async () => {
    await fetchSubscriptionData();
  };

  return {
    isInitialized,
    subscription,
    hasActiveSubscription: hasActive,
    isLoading,
    error,
    refetch,
  };
};
